import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { toast } from '@/components/ui/toast'
import { cn } from '@/lib/utils'
import { jobsApi } from '@/services/jobs'
import { getApiErrorMessage } from '@/services/api'
import { isValidEmail } from '@/lib/validations/common'
import { usePermissions } from '@/hooks/usePermissions'

// 2.3.8 / 2.4.10 Export & send job report — send popup with validation.
export function SendReportDialog({
  jobId,
  defaultEmail,
  defaultBody = '',
  onClose,
}: {
  jobId: string
  defaultEmail: string
  defaultBody?: string
  onClose: () => void
}) {
  const { t } = useTranslation('jobs')
  const queryClient = useQueryClient()
  const { canEdit } = usePermissions()
  const canSendReports = canEdit('agenda')
  const [destinationEmail, setDestinationEmail] = useState(defaultEmail)
  const [cc, setCc] = useState('')
  const [body, setBody] = useState(defaultBody)
  const [error, setError] = useState('')
  const [sending, setSending] = useState(false)

  useEffect(() => {
    if (!canSendReports) onClose()
  }, [canSendReports, onClose])

  if (!canSendReports) return null

  const send = async () => {
    if (!destinationEmail || !isValidEmail(destinationEmail.trim())) {
      setError(t('sendReport.invalidRecipient'))
      return
    }
    const extras = cc
      .split(',')
      .map(s => s.trim())
      .filter(Boolean)
    if (extras.some(e => !isValidEmail(e))) {
      toast({
        variant: 'destructive',
        title: t('sendReport.invalidAdditional'),
      })
      return
    }
    setError('')
    setSending(true)
    try {
      const reportResponse = await jobsApi.sendReport(jobId, {
        recipient_email: destinationEmail,
        recipient_emails: extras.length ? extras : undefined,
        message: body || undefined,
      })
      queryClient.setQueryData(['jobs', jobId, 'report'], reportResponse)
      queryClient.invalidateQueries({ queryKey: ['jobs', jobId, 'report'] })
      queryClient.invalidateQueries({ queryKey: ['jobs', 'detail', jobId] })
      queryClient.invalidateQueries({ queryKey: ['jobs'] })
      queryClient.invalidateQueries({ queryKey: ['agenda'] })
      toast({ variant: 'success', title: t('sendReport.sendSuccess') })
      onClose()
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(e, t('sendReport.sendError')),
      })
    } finally {
      setSending(false)
    }
  }

  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('sendReport.title')}</DialogTitle>
        </DialogHeader>
        <div className='space-y-3'>
          <div className='space-y-2'>
            <Label htmlFor='destination-email'>{t('sendReport.to')}</Label>
            <Input
              id='destination-email'
              value={destinationEmail}
              onChange={e => setDestinationEmail(e.target.value)}
              className={cn(
                error && 'border-destructive focus-visible:ring-destructive'
              )}
            />
            {error && <p className='text-xs text-destructive'>{error}</p>}
          </div>
          <div className='space-y-2'>
            <Label htmlFor='cc'>{t('sendReport.additionalRecipients')}</Label>
            <Input
              id='cc'
              placeholder={t('sendReport.commaSeparated')}
              value={cc}
              onChange={e => setCc(e.target.value)}
            />
          </div>
          <div className='space-y-2'>
            <Label htmlFor='body'>{t('sendReport.emailBody')}</Label>
            <Textarea
              id='body'
              rows={4}
              maxLength={5000}
              value={body}
              onChange={e => setBody(e.target.value)}
            />
          </div>
          <p className='text-xs text-muted-foreground'>
            {t('sendReport.pdfAttached')}
          </p>
        </div>
        <DialogFooter>
          <Button variant='outline' onClick={onClose}>
            {t('sendReport.cancel')}
          </Button>
          <Button disabled={sending} onClick={send}>
            {t('sendReport.send')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
